import { useEffect, useRef, useState } from 'react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';

// Kebab (⋮) action menu for table rows.

export interface RowMenuItem {
    label: string;
    icon?: React.ReactNode;
    trailing?: React.ReactNode;
    tone?: 'default' | 'danger';
    onClick: () => void;
    /** Renders the item greyed-out and inert, with `disabledReason` beside or wrapped under the label. */
    disabled?: boolean;
    disabledReason?: string;
}

// Compact is one text line tall, to sit beside the small toggles of a panel header.
const TRIGGER_STYLES = {
    row: {
        button: 'w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors',
        icon: 'h-5 w-5'
    },
    compact: {
        button: 'flex items-center rounded-md border border-gray-200 bg-white px-1 py-0.5 text-[11px] text-gray-500 hover:text-gray-700 transition-colors',
        icon: 'h-[1.5em] w-3.5'
    }
} as const;

export function RowMenu({
    items,
    label = 'Row actions',
    size = 'row'
}: {
    items: RowMenuItem[];
    label?: string;
    size?: keyof typeof TRIGGER_STYLES;
}) {
    const [open, setOpen] = useState(false);
    // Open upward when the trigger sits near the viewport bottom, so the
    // menu isn't cut off by the table's scroll container on the last rows.
    const [openUp, setOpenUp] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const toggle = () => {
        if (!open && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setOpenUp(rect.bottom > window.innerHeight - 260);
        }
        setOpen((v) => !v);
    };

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        // Capture phase, so the menu claims Escape before a host panel's own listener closes it.
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
            triggerRef.current?.focus();
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, [open]);

    return (
        <div ref={containerRef} className="relative" onClick={(e) => e.stopPropagation()}>
            <button
                ref={triggerRef}
                type="button"
                aria-label={label}
                onClick={toggle}
                className={TRIGGER_STYLES[size].button}
            >
                <EllipsisVerticalIcon className={TRIGGER_STYLES[size].icon} />
            </button>
            {open && (
                <div
                    className={`absolute right-0 z-30 w-max min-w-[12rem] max-w-xs bg-white border border-gray-200 rounded-xl shadow-lg p-1.5 ${
                        openUp ? 'bottom-full mb-1' : 'top-full mt-1'
                    }`}
                >
                    {items.map((item) => (
                        <button
                            key={item.label}
                            type="button"
                            disabled={item.disabled}
                            onClick={() => {
                                if (item.disabled) return;
                                setOpen(false);
                                item.onClick();
                            }}
                            className={`flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                                item.disabled
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : `hover:bg-gray-50 ${item.tone === 'danger' ? 'text-red-600' : 'text-gray-700'}`
                            }`}
                        >
                            {item.icon}
                            <span className="flex flex-1 flex-wrap items-baseline justify-between gap-x-2">
                                <span className="flex items-center gap-1.5 font-medium">
                                    {item.label}
                                    {item.trailing}
                                </span>
                                {item.disabled && item.disabledReason && (
                                    <span className="text-[11px] text-gray-400">
                                        {item.disabledReason}
                                    </span>
                                )}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
